import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { PublicKey, Transaction } from '@solana/web3.js';
import {
  createTransferCheckedInstruction,
  getAssociatedTokenAddress,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import bs58 from 'bs58';
import { apiFetch, getApiHost } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent, trackError } from '../utils/analytics';
import { redirectToPhantomIfMobile } from '../utils/phantomMobile';
import {
  persistentPhantomWalletMatches,
  startPhantomSignTransaction,
  consumePhantomTxResult,
} from '../utils/phantomDeeplink';
import {
  hasLiveWCSessionSync,
  signTransactionViaWC,
} from '../utils/walletConnectMobile';

// Mirrors BurnCredits.tsx but transfers USDC instead of burning $DNET.
// USDC is $1 → no slippage tolerance to manage.

interface CryptoPricing {
  usdPerCredit: number;
  usdcMint: string;
  usdcDecimals: number;
  payTo: string;
  payToUsdcAta: string;
  bundles: {
    credits: number;
    discount: number;
    label: string;
    totalUsdc: number;
    totalUsdcAtomic: number;
  }[];
}

interface Props {
  onCreditsAdded: () => void;
}

const CryptoBuyCredits: React.FC<Props> = ({ onCreditsAdded }) => {
  const { publicKey, signTransaction } = useWallet();
  const { setVisible: setWalletModalVisible } = useWalletModal();
  const { user } = useAuth();

  const authWalletAddress = user?.walletAddress ?? null;
  const displayWalletAddress = publicKey?.toBase58() ?? authWalletAddress;

  const [pricing, setPricing] = useState<CryptoPricing | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [loadingPricing, setLoadingPricing] = useState(true);

  const [selectedBundle, setSelectedBundle] = useState<number>(0);
  const [paying, setPaying] = useState(false);
  const [payStatus, setPayStatus] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [paySuccess, setPaySuccess] = useState<{ credits: number; txSignature: string } | null>(null);

  // Pre-flight USDC balance for the connected wallet. Without this the
  // user only finds out they have no USDC when their wallet rejects the
  // tx simulation with a cryptic "InvalidAccountData" error.
  const [usdcBalance, setUsdcBalance] = useState<number | null>(null);
  const [usdcAtaExists, setUsdcAtaExists] = useState<boolean | null>(null);

  const fetchPricing = useCallback(async () => {
    try {
      setLoadingPricing(true);
      setPricingError(null);
      const res = await apiFetch(`${getApiHost()}/api/crypto-credits/price`);
      if (!res.ok) throw new Error('Failed to fetch pricing');
      setPricing(await res.json());
    } catch (err) {
      setPricingError(err instanceof Error ? err.message : 'Failed to load pricing');
    } finally {
      setLoadingPricing(false);
    }
  }, []);

  useEffect(() => { fetchPricing(); }, [fetchPricing]);

  // Re-check USDC balance whenever the connected wallet changes.
  const walletAddress = displayWalletAddress;
  useEffect(() => {
    if (!walletAddress) { setUsdcBalance(null); setUsdcAtaExists(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${getApiHost()}/api/crypto-credits/wallet-status/${walletAddress}`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setUsdcBalance(data.balanceUsdc);
        setUsdcAtaExists(data.ataExists);
      } catch { /* ignore — pay button will still attempt and surface error */ }
    })();
    return () => { cancelled = true; };
  }, [walletAddress]);

  const submitSignedTx = useCallback(async (serializedBase64: string, expectedCredits: number) => {
    setPayStatus('Sending transaction…');
    const sendRes = await apiFetch(`${getApiHost()}/api/burn/send-transaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transaction: serializedBase64 }),
    });
    if (!sendRes.ok) {
      const errData = await sendRes.json().catch(() => ({}));
      throw new Error(errData.error || 'Failed to send transaction');
    }
    const { signature } = await sendRes.json();

    setPayStatus('Verifying transfer and granting credits…');
    const token = localStorage.getItem('deepnets_token');
    const verifyRes = await apiFetch(`${getApiHost()}/api/crypto-credits/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ txSignature: signature, expectedCredits }),
    });
    if (!verifyRes.ok) {
      const errData = await verifyRes.json().catch(() => ({}));
      throw new Error(errData.error || 'Verification failed');
    }
    const result = await verifyRes.json();
    setPaySuccess({ credits: result.creditsGranted, txSignature: signature });
    setPayStatus(null);
    onCreditsAdded();
  }, [onCreditsAdded]);

  // Pick up Phantom-deeplink-tx continuations on mount.
  const continuationRanRef = useRef(false);
  useEffect(() => {
    if (continuationRanRef.current) return;
    if (!new URLSearchParams(window.location.search).has('phantom-tx-pending')) return;
    continuationRanRef.current = true;

    const tx = consumePhantomTxResult('buy-credits');
    if (!tx) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('phantom-tx-pending');
    window.history.replaceState({}, '', url.toString());

    if (tx.error) {
      setPayError(tx.error.toLowerCase().includes('cancel') ? 'Transaction cancelled' : tx.error);
      setPaying(false);
      return;
    }
    if (!tx.signedTransactionBase58) {
      setPayError('Phantom returned no signed transaction');
      setPaying(false);
      return;
    }
    const expectedCredits = Number(tx.params.credits) || 0;
    const txBytes = bs58.decode(tx.signedTransactionBase58);
    const base64 = btoa(String.fromCharCode(...txBytes));
    setPaying(true);
    setPayError(null);
    setPaySuccess(null);
    submitSignedTx(base64, expectedCredits).catch(err => {
      const msg = err?.message || 'Payment failed';
      // Don't report user cancellations as errors — only real failures.
      if (!msg.includes('User rejected')) trackError(err, { flow: 'crypto_credit_pay', credits: expectedCredits });
      setPayError(msg.includes('User rejected') ? 'Transaction cancelled' : msg);
      setPayStatus(null);
    }).finally(() => setPaying(false));
  }, [submitSignedTx]);

  const handlePay = async () => {
    if (!pricing) return;

    const bundle = pricing.bundles[selectedBundle];
    if (!bundle) return;

    trackEvent('buy_credits_clicked', { method: 'crypto', bundle: bundle.credits, amountUsd: bundle.totalUsdc });

    // Mobile fast path: persistent Phantom session for the SAME wallet
    // the user is currently authed with → bounce to Phantom's
    // signTransaction directly, skipping the wallet-adapter modal.
    // Wallet-match guard prevents a Jupiter-WC-signed-in user from
    // being silently routed to a stale Phantom account for the payment.
    const usePhantomDeeplink = !publicKey && persistentPhantomWalletMatches(authWalletAddress);
    if (usePhantomDeeplink) {
      try {
        setPaying(true);
        setPayError(null);
        setPaySuccess(null);
        setPayStatus('Building transaction…');
        const burnerPubkey = new PublicKey(authWalletAddress!);
        const usdcMint = new PublicKey(pricing.usdcMint);
        const destAta = new PublicKey(pricing.payToUsdcAta);
        const sourceAta = await getAssociatedTokenAddress(usdcMint, burnerPubkey, false, TOKEN_PROGRAM_ID);

        const transferIx = createTransferCheckedInstruction(
          sourceAta, usdcMint, destAta, burnerPubkey,
          BigInt(bundle.totalUsdcAtomic), pricing.usdcDecimals, [], TOKEN_PROGRAM_ID,
        );

        const blockhashRes = await apiFetch(`${getApiHost()}/api/burn/blockhash`);
        if (!blockhashRes.ok) throw new Error('Could not fetch blockhash');
        const { blockhash } = await blockhashRes.json();

        const tx = new Transaction().add(transferIx);
        tx.recentBlockhash = blockhash;
        tx.feePayer = burnerPubkey;

        const transactionBase58 = bs58.encode(tx.serialize({ requireAllSignatures: false }));
        startPhantomSignTransaction({
          transactionBase58,
          operation: 'buy-credits',
          params: { credits: bundle.credits },
          returnUrl: window.location.pathname + window.location.search,
        });
        return;
      } catch (err: any) {
        setPayError(err?.message || 'Payment failed');
        setPayStatus(null);
        setPaying(false);
        return;
      }
    }

    // Mobile fast path #2: live WC session (Jupiter etc) for the same
    // auth wallet → sign over the relay, await on the same page.
    const useWalletConnect = !publicKey && !!authWalletAddress && hasLiveWCSessionSync(authWalletAddress);
    if (useWalletConnect) {
      setPaying(true);
      setPayError(null);
      setPaySuccess(null);
      setPayStatus('Building transaction…');
      try {
        const burnerPubkey = new PublicKey(authWalletAddress!);
        const usdcMint = new PublicKey(pricing.usdcMint);
        const destAta = new PublicKey(pricing.payToUsdcAta);
        const sourceAta = await getAssociatedTokenAddress(usdcMint, burnerPubkey, false, TOKEN_PROGRAM_ID);

        const transferIx = createTransferCheckedInstruction(
          sourceAta, usdcMint, destAta, burnerPubkey,
          BigInt(bundle.totalUsdcAtomic), pricing.usdcDecimals, [], TOKEN_PROGRAM_ID,
        );

        const blockhashRes = await apiFetch(`${getApiHost()}/api/burn/blockhash`);
        if (!blockhashRes.ok) throw new Error('Could not fetch blockhash');
        const { blockhash } = await blockhashRes.json();

        const tx = new Transaction().add(transferIx);
        tx.recentBlockhash = blockhash;
        tx.feePayer = burnerPubkey;

        const transactionBase58 = bs58.encode(tx.serialize({ requireAllSignatures: false }));
        setPayStatus('Approve in your wallet…');
        const signedBytes = await signTransactionViaWC(transactionBase58);
        const base64 = btoa(String.fromCharCode(...signedBytes));
        await submitSignedTx(base64, bundle.credits);
      } catch (err: any) {
        const msg = err?.message || 'Payment failed';
        const looksLikeNoUsdc = msg.includes('InvalidAccountData') || msg.includes('invalid account data');
        if (msg.toLowerCase().includes('reject') || msg.toLowerCase().includes('cancel')) {
          setPayError('Transaction cancelled');
        } else if (looksLikeNoUsdc) {
          setPayError('No USDC found in this wallet. Fund it with USDC on Solana, then try again.');
        } else {
          setPayError(msg);
        }
        setPayStatus(null);
      } finally {
        // Make sure the spinner clears whether we succeeded or threw.
        setPaying(false);
      }
      return;
    }

    if (!publicKey || !signTransaction) {
      // No matching mobile sign path for the auth wallet.
      if (authWalletAddress) {
        setPayError(
          'No active wallet session for signing. Sign out and sign in again with the wallet you want to pay from.'
        );
        return;
      }
      if (redirectToPhantomIfMobile()) return;
      setWalletModalVisible(true);
      return;
    }

    if (authWalletAddress && publicKey.toBase58() !== authWalletAddress) {
      setPayError(
        `Connected wallet doesn't match the wallet on your account (${authWalletAddress.slice(0, 4)}…${authWalletAddress.slice(-4)})`
      );
      return;
    }

    setPaying(true);
    setPayError(null);
    setPaySuccess(null);
    setPayStatus('Building transaction…');

    try {
      const usdcMint = new PublicKey(pricing.usdcMint);
      const destAta = new PublicKey(pricing.payToUsdcAta);
      const sourceAta = await getAssociatedTokenAddress(usdcMint, publicKey, false, TOKEN_PROGRAM_ID);

      const transferIx = createTransferCheckedInstruction(
        sourceAta, usdcMint, destAta, publicKey,
        BigInt(bundle.totalUsdcAtomic), pricing.usdcDecimals, [], TOKEN_PROGRAM_ID,
      );

      setPayStatus('Preparing transaction…');
      const blockhashRes = await apiFetch(`${getApiHost()}/api/burn/blockhash`);
      if (!blockhashRes.ok) throw new Error('Could not fetch blockhash');
      const { blockhash } = await blockhashRes.json();

      const tx = new Transaction().add(transferIx);
      tx.recentBlockhash = blockhash;
      tx.feePayer = publicKey;

      setPayStatus('Approve the USDC transfer in your wallet…');
      const signed = await signTransaction(tx);

      const serialized = signed.serialize().toString('base64');
      await submitSignedTx(serialized, bundle.credits);
    } catch (err: any) {
      console.error('Crypto pay error:', err);
      const msg = err?.message || 'Payment failed';
      const looksLikeNoUsdc =
        msg.includes('InvalidAccountData') ||
        msg.includes('invalid account data');
      if (msg.includes('User rejected') || msg.includes('rejected the request')) {
        setPayError('Transaction cancelled');
      } else if (looksLikeNoUsdc) {
        setPayError('No USDC found in this wallet. Fund it with USDC on Solana, then try again.');
      } else if (msg.toLowerCase().includes('insufficient')) {
        setPayError(`${msg} — make sure your wallet has enough USDC and a small SOL balance for gas.`);
      } else {
        setPayError(msg);
      }
      setPayStatus(null);
    } finally {
      setPaying(false);
    }
  };

  if (loadingPricing) {
    return (
      <div className="text-center py-4">
        <div className="animate-spin w-6 h-6 border-2 border-crypto-accent border-t-transparent rounded-full mx-auto mb-2"></div>
        <p className="text-gray-400 text-sm">Loading pricing…</p>
      </div>
    );
  }

  if (pricingError) {
    return (
      <div className="text-center py-4">
        <p className="text-red-400 text-sm mb-2">Could not load pricing</p>
        <button onClick={fetchPricing} className="text-crypto-accent hover:text-crypto-accent/80 text-sm">
          Try again
        </button>
      </div>
    );
  }

  if (!pricing) return null;

  const currentBundle = pricing.bundles[selectedBundle];
  const insufficientUsdc =
    usdcBalance != null && currentBundle && usdcBalance < currentBundle.totalUsdc;
  const noUsdcAtAll = usdcAtaExists === false;

  return (
    <div className="space-y-4">
      {walletAddress && usdcBalance != null && (
        <div className={`text-xs px-3 py-2 rounded-lg border ${
          noUsdcAtAll
            ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
            : insufficientUsdc
              ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
              : 'bg-crypto-dark border-gray-700 text-gray-400'
        }`}>
          {noUsdcAtAll
            ? 'No USDC found in this wallet. Fund it with USDC on Solana before paying.'
            : `Wallet balance: ${usdcBalance.toFixed(2)} USDC`}
        </div>
      )}
      <div className="space-y-2">
        {pricing.bundles.map((b, i) => (
          <button
            key={b.credits}
            onClick={() => setSelectedBundle(i)}
            className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors text-left ${
              selectedBundle === i
                ? 'border-crypto-accent bg-crypto-accent/10'
                : 'border-gray-700 bg-crypto-dark hover:border-gray-600'
            }`}
          >
            <div>
              <span className="text-white font-medium">{b.label}</span>
              {b.discount > 0 && (
                <span className="ml-2 text-xs text-crypto-accent">Save {Math.round(b.discount * 100)}%</span>
              )}
            </div>
            <div className="text-right">
              <div className="text-white font-mono text-sm">{b.totalUsdc.toFixed(2)} USDC</div>
              <div className="text-gray-500 text-xs">~${b.totalUsdc.toFixed(2)}</div>
            </div>
          </button>
        ))}
      </div>

      <button
        onClick={handlePay}
        disabled={paying || !displayWalletAddress || noUsdcAtAll || !!insufficientUsdc}
        className="w-full bg-crypto-accent hover:bg-crypto-accent/80 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-crypto-dark font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        {paying ? (
          <>
            <div className="animate-spin w-4 h-4 border-2 border-crypto-dark border-t-transparent rounded-full" />
            <span>{payStatus || 'Processing…'}</span>
          </>
        ) : (
          <>
            <span>🪙</span>
            <span>
              Pay {currentBundle?.totalUsdc.toFixed(2)} USDC for {currentBundle?.credits} Credit{currentBundle?.credits !== 1 ? 's' : ''}
            </span>
          </>
        )}
      </button>

      {!displayWalletAddress && (
        <p className="text-gray-500 text-xs text-center">Connect your Solana wallet to pay with USDC</p>
      )}

      {payError && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{payError}</p>
        </div>
      )}

      {paySuccess && (
        <div className="p-3 bg-crypto-accent/10 border border-crypto-accent/30 rounded-lg">
          <p className="text-crypto-accent text-sm font-medium">
            ✓ Paid! +{paySuccess.credits} credit{paySuccess.credits !== 1 ? 's' : ''} added
          </p>
          <a
            href={`https://solscan.io/tx/${paySuccess.txSignature}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-white text-xs mt-1 block transition-colors"
          >
            View transaction →
          </a>
        </div>
      )}

      <p className="text-gray-600 text-xs text-center">
        USDC on Solana mainnet. You'll need a small SOL balance for the network fee (~$0.001).
      </p>
    </div>
  );
};

export default CryptoBuyCredits;
